import { signInWithEmailAndPassword, signOut, onAuthStateChanged, sendPasswordResetEmail, User as FirebaseUser } from 'firebase/auth'
import { auth } from '../../config/firebase'
import { environment } from '../../config/environment'
import type { User } from '../../types/auth'
import { UserConnectionState } from '../../types/auth'
import { logger } from '../../utils/logger'

export class AuthService {
  /**
   * Connexion avec email/password
   */
  static async login(email: string, password: string): Promise<User> {
    const mode = environment.getConfig().mode

    logger.log('🔐 AuthService.login called with mode:', mode)

    if (mode === 'fake') {
      // Mode fake : utiliser la logique mockée
      logger.log('🎭 Using fake authentication')
      return AuthService.mockLogin(email, password)
    } else {
      // Mode dev/prod : utiliser Firebase Auth
      logger.log('🔥 Using Firebase authentication')
      try {
        const userCredential = await signInWithEmailAndPassword(auth, email, password)
        const firebaseUser = userCredential.user
        
        logger.log('✅ Firebase login successful:', firebaseUser.uid)
        
        // Convertir l'utilisateur Firebase en utilisateur de l'app
        return AuthService.convertFirebaseUser(firebaseUser)
      } catch (error: any) {
        logger.error('❌ Firebase login error:', error.code, error.message)
        throw new Error(AuthService.getFirebaseErrorMessage(error.code))
      }
    }
  }

  /**
   * Déconnexion
   */
  static async logout(): Promise<void> {
    const mode = environment.getConfig().mode

    if (mode === 'fake') {
      // Mode fake : rien à faire
      return
    } else {
      // Mode dev/prod : déconnexion Firebase
      await signOut(auth)
    }
  }

  /**
   * Réinitialisation du mot de passe
   */
  static async resetPassword(email: string): Promise<void> {
    const mode = environment.getConfig().mode

    logger.log('🔐 AuthService.resetPassword called with mode:', mode)

    if (mode === 'fake') {
      // Mode fake : simuler l'envoi
      logger.log('🎭 Using fake password reset')
      await new Promise(resolve => setTimeout(resolve, 1000))
      return
    } else {
      // Mode dev/prod : utiliser Firebase Auth
      logger.log('🔥 Using Firebase password reset')
      try {
        await sendPasswordResetEmail(auth, email)
        logger.log('✅ Password reset email sent to:', email)
      } catch (error: any) {
        logger.error('❌ Password reset error:', error.code, error.message)
        throw new Error(AuthService.getFirebaseErrorMessage(error.code))
      }
    }
  }

  /**
   * Écouter les changements d'état d'authentification
   */
  static onAuthStateChanged(callback: (user: User | null) => void): () => void {
    const mode = environment.getConfig().mode

    if (mode === 'fake') {
      // Mode fake : pas d'écoute
      return () => {}
    } else {
      // Mode dev/prod : écouter Firebase Auth
      return onAuthStateChanged(auth, (firebaseUser) => {
        if (firebaseUser) {
          const user = AuthService.convertFirebaseUser(firebaseUser)
          callback(user)
        } else {
          callback(null)
        }
      })
    }
  }

  /**
   * Authentification mockée pour le mode fake
   */
  private static async mockLogin(email: string, password: string): Promise<User> {
    // Simuler un délai réseau
    await new Promise(resolve => setTimeout(resolve, 1000))

    if (email === 'admin@skeenfit.com' && password === 'password') {
      return {
        uid: 'mock-uid-1',
        id: 'mock-id-1',
        firstName: 'Admin',
        lastName: 'User',
        email,
        userConnectionState: UserConnectionState.CONNECTED,
        isNewUser: false,
        isAdmin: true,
        name: 'Admin User',
        role: 'admin',
        initials: 'AU',
      }
    } else {
      throw new Error('Identifiants incorrects')
    }
  }

  /**
   * Convertir un utilisateur Firebase en utilisateur de l'app
   */
  private static convertFirebaseUser(firebaseUser: FirebaseUser): User {
    const displayName = firebaseUser.displayName || ''
    const [firstName, ...lastNameParts] = displayName.split(' ')
    const lastName = lastNameParts.join(' ')

    return {
      uid: firebaseUser.uid,
      id: firebaseUser.uid,
      firstName: firstName || 'Utilisateur',
      lastName: lastName || '',
      email: firebaseUser.email || '',
      userConnectionState: UserConnectionState.CONNECTED,
      isNewUser: false, // TODO: déterminer si c'est un nouvel utilisateur
      isAdmin: true, // TODO: récupérer le rôle depuis la base de données
      name: displayName || firebaseUser.email || 'Utilisateur',
      role: 'admin', // TODO: récupérer le rôle depuis la base de données
      initials: AuthService.getInitials(firstName, lastName),
    }
  }

  /**
   * Générer les initiales à partir du prénom et nom
   */
  private static getInitials(firstName: string, lastName: string): string {
    const first = firstName?.charAt(0)?.toUpperCase() || ''
    const last = lastName?.charAt(0)?.toUpperCase() || ''
    return first + last || 'U'
  }

  /**
   * Convertir les codes d'erreur Firebase en messages compréhensibles
   */
  private static getFirebaseErrorMessage(errorCode: string): string {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'Aucun utilisateur trouvé avec cet email'
      case 'auth/wrong-password':
        return 'Mot de passe incorrect'
      case 'auth/invalid-email':
        return 'Email invalide'
      case 'auth/user-disabled':
        return 'Ce compte a été désactivé'
      case 'auth/too-many-requests':
        return 'Trop de tentatives de connexion. Réessayez plus tard'
      case 'auth/network-request-failed':
        return 'Erreur de connexion réseau'
      case 'auth/invalid-credential':
        return 'Identifiants invalides'
      default:
        return 'Erreur de connexion'
    }
  }
}